import React, { ReactNode, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { ModalProps } from 'react-bootstrap/Modal';
import { FormattedMessage } from 'react-intl';

import Loader from '@app/partials/content/Loader';
import ModalDefault from '@app/partials/content/modals/Modal.default';

type ConfirmModalProps = {
    title: ReactNode;
    description: ReactNode;
    showModal: boolean;
    setShowModal: (value: boolean) => void;
    onConfirm?: () => void;
    onCancel?: () => void;
    loading?: boolean;
    validationCheckbox?: boolean;
    icon?: ReactNode;
    confirmLabel?: ReactNode;
    cancelLabel?: ReactNode;
    checkboxLabel?: ReactNode;
    loadingTitle?: ReactNode;
    loadingDescription?: ReactNode;
    size?: ModalProps['size'];
};

const ConfirmModal = ({
    icon,
    title,
    description,
    onConfirm,
    onCancel,
    showModal,
    setShowModal,
    loading,
    validationCheckbox,
    checkboxLabel = <FormattedMessage id="TRANSLATOR.CONFIRM" />,
    loadingTitle = <FormattedMessage id="TRANSLATOR.LOADING" />,
    confirmLabel = <FormattedMessage id="TRANSLATOR.VALIDATE" />,
    cancelLabel = <FormattedMessage id="TRANSLATOR.BACK" />,
    loadingDescription,
    size,
}: ConfirmModalProps) => {
    const [confirmationValid, setConfirmationValid] = useState(false);

    useEffect(() => {
        setConfirmationValid(false);
    }, [showModal]);

    const handleCancel = () => {
        if (onCancel) onCancel();
        else setShowModal(false);
    };

    const handleConfirm = () => {
        if (onConfirm) onConfirm();
        else setShowModal(false);
    };

    const confirmDisabled = (validationCheckbox === true && confirmationValid === false) || loading;

    return (
        <ModalDefault
            show={showModal}
            hideModal={() => {
                setShowModal(false);
            }}
            backdrop="static"
            closeButton={!loading}
            icon={icon}
            title={loading ? loadingTitle : title}
            size={size}
            body={
                <div className="d-flex justify-content-center align-items-center flex-column">
                    {loading ? (
                        <>
                            {loadingDescription && <div className="text-center">{loadingDescription}</div>}
                            <Loader style={{ width: '5rem', height: '5rem' }} block />
                        </>
                    ) : (
                        <>
                            <div className="text-center">{description}</div>

                            {validationCheckbox && (
                                <div className="form-check mt-5">
                                    <input
                                        aria-labelledby="validationCheckboxLabel"
                                        className="form-check-input"
                                        name="validationCheckbox"
                                        type="checkbox"
                                        id="validationCheckbox"
                                        onChange={(e) => setConfirmationValid(e.target.checked)}
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor="validationCheckbox"
                                        id="validationCheckboxLabel"
                                    >
                                        {checkboxLabel}
                                    </label>
                                </div>
                            )}
                        </>
                    )}
                </div>
            }
            footer={
                <div className="d-flex justify-content-center">
                    <Button variant="outline-primary" disabled={loading} onClick={handleCancel} className="mr-5">
                        {cancelLabel}
                    </Button>
                    <Button variant="primary" disabled={confirmDisabled} onClick={handleConfirm}>
                        {confirmLabel}
                    </Button>
                </div>
            }
        />
    );
};

export default ConfirmModal;
